import { isValid, parseISO, format } from "date-fns";

import { useStore } from "@/store/store";
import { formatLocalDate } from "./date";
import { isAdminLike } from "./routingUtils";

export type TModalSize = "xs" | "sm" | "md" | "lg" | "xl" | "4xl" | "full";
type TFormatDateProps = {
  date: string | null | undefined;
};

// get Date format  based on APi
export function getDateFormatByLocale(
  type: "single" | "range" = "single",
  uppercase = false,
) {
  const localeVar = useStore.getState().globalDateFormat
    ? useStore.getState().globalDateFormat
    : "en-US"; // fallback
  const sampleDate = new Date(2000, 4, 21); // May 21, 2000
  let formatter: Intl.DateTimeFormat;
  try {
    formatter = new Intl.DateTimeFormat(localeVar, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch (error) {
    console.log("locals not supported", error);
    formatter = new Intl.DateTimeFormat("en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }

  const parts = formatter.formatToParts(sampleDate);

  const format = parts
    .map((part) => {
      switch (part.type) {
        case "day":
          return uppercase ? "DD" : "dd";
        case "month":
          return "MM";
        case "year":
          return uppercase ? "YYYY" : "yyyy";
        case "literal":
          return part.value;
        default:
          return "";
      }
    })
    .join("");

  return type === "range" ? `${format} - ${format}` : format;
}

// convert and return format based on locals.
export function formatLocaleDate(date: Date): string {
  const format = getDateFormatByLocale();

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear());

  return format.replace("dd", day).replace("MM", month).replace("yyyy", year);
}

export const getSizeClasses = (size: TModalSize) => {
  switch (size) {
    case "xs":
      return "max-w-xs";
    case "sm":
      return "max-w-sm";
    case "md":
      return "max-w-md";
    case "lg":
      return "max-w-lg";
    case "xl":
      return "max-w-xl";
    case "4xl":
      return "max-w-4xl";
    case "full":
      return "max-w-full";
    default:
      return "max-w-md";
  }
};

/**
 * Universal Input Sanitizer
 * Strips HTML tags, SQL injection characters, and dangerous scripts.
 */
export const sanitizeInput = (val: unknown): string => {
  if (val === null || val === undefined) return "";

  let str = "";
  if (typeof val === "object") {
    try {
      str = JSON.stringify(val);
    } catch {
      str = "";
    }
  } else if (typeof val === "symbol") {
    str = val.toString();
  } else if (
    typeof val === "string" ||
    typeof val === "number" ||
    typeof val === "boolean" ||
    typeof val === "bigint"
  ) {
    str = String(val);
  } else {
    str = "";
  }

  return str
    .replace(/<[^>]*>/g, "")
    .replace(/[;'"\\]/g, "")
    .replace(/--/g, "")
    .replace(/\s*on\w+\s*=\s*(['"]).*?\1/gi, "")
    .replace(/javascript:/gi, "")
    .replace(/\s+/g, " ")
    .substring(0, 5000);
};
export const isBlankInputValue = (val: unknown): boolean => {
  return sanitizeInput(val).trim().length === 0;
};

// Function to build query parameters from an object
export function buildQueryParams(params: Record<string, any>): string {
  const queryParams = Object.entries(params)
    .filter(([_, value]) => value !== undefined && value !== null)
    .flatMap(([key, value]) => {
      if (Array.isArray(value)) {
        return value.map((v) => `${key}=${encodeURIComponent(String(v))}`);
      } else if (typeof value === "object") {
        return [`${key}=${encodeURIComponent(JSON.stringify(value))}`];
      } else {
        return [`${key}=${encodeURIComponent(String(value))}`];
      }
    })
    .join("&");

  return queryParams ? `?${queryParams}` : "";
}

export const formatDate = (dateString: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB");
};

export const formatDateTime = (dateString: string) => {
  if (!dateString) return "";
  const date = new Date(dateString.replace(/\.\d+/, ""));
  return format(date, "dd/MM/yyyy hh:mm a");
};

/**
 * Keys whose values are ISO datetime strings from TruncMonth/TruncWeek/TruncDate/TruncYear.
 * Covers all existing temporal dimensions including new ingestion_* ones.
 */
const TEMPORAL_DIMENSION_GRANULARITY: Record<
  string,
  "date" | "week" | "month" | "year"
> = {
  call_date: "date",
  call_week: "week",
  call_month: "month",
  call_year: "year",
  evaluation_date: "date",
  evaluation_week: "week",
  evaluation_month: "month",
  evaluation_year: "year",
  ingestion_day: "date",
  ingestion_week: "week",
  ingestion_month: "month",
};

/**
 * Format a raw dimension value for display.
 * Temporal dimensions come back as ISO datetime strings (e.g. "2026-02-01T00:00:00+05:30");
 * we humanise them based on the granularity of the dimension key.
 * All other values are returned as-is.
 */
export function formatDimensionValue(dimensionKey: string, value: any): string {
  if (value === null || value === undefined) return "N/A";
  const granularity = TEMPORAL_DIMENSION_GRANULARITY[dimensionKey];
  if (!granularity) return String(value);

  // Strip timezone offset before parsing to avoid local-offset surprises
  const isoStr = typeof value === "string" ? value : String(value);
  const date = new Date(isoStr);
  if (isNaN(date.getTime())) return String(value);

  try {
    if (granularity === "year") {
      return date.toLocaleDateString("en-GB", {
        year: "numeric",
        timeZone: "UTC",
      });
    }
    if (granularity === "month") {
      return date.toLocaleDateString("en-GB", {
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      });
    }
    if (granularity === "week") {
      // Show start-of-week date
      return (
        "Wk " +
        date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          timeZone: "UTC",
        })
      );
    }
    // date granularity
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    });
  } catch {
    return String(value);
  }
}

export function commonDateFormat(date: string, formatString: string) {
  if (!date) return "";
  try {
    return format(new Date(date), formatString);
  } catch (err) {
    console.error("Invalid date format:", err);
    return "";
  }
}

// Function to generate a short name from first and last names
export const userShortName = (
  firstName: string | null,
  lastName: string | null,
): string => {
  if (!firstName && !lastName) return "NA";
  const first = firstName ? firstName.charAt(0).toUpperCase() : "";
  const last = lastName ? lastName.charAt(0).toUpperCase() : "";
  return `${first}${last}`;
};

// Function to format a date to "DD/MM/YYYY"
export const formatDateTwoDigit = (date: TFormatDateProps["date"]): string => {
  if (!date) return "-";
  const d = new Date(date);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

// Function to clean and format the raw dialogue text
export function cleanAndFormatDialogue(raw: unknown) {
  if (typeof raw !== "string") return [];

  // Step 1: Remove all [number] patterns using regex
  const noTimestamps = raw.replace(/\[\d+(\.\d+)?\]/g, "");

  // Step 2: Add <br> after every speaker line (optional fine-tune)
  const withLineBreaks = noTimestamps.replace(/\n/g, "<br>").trim();

  // return withLineBreaks;
  return parseDialogue(withLineBreaks);
}

// Function to parse the dialogue text into a structured format
export function parseDialogue(data: unknown) {
  if (typeof data !== "string") return [];
  const dialogues: string[] = data.split("<br>");
  return dialogues.map((line: string) => {
    const [role, ...text] = line.split(": ");
    return {
      role: role?.trim(),
      text: text.join(": ").trim(),
    };
  });
}

export const getCategoryObj = () => {
  const categoryObj = {
    category: {
      order: 0,
      internal_id: "",
      name: "",
      description: "",
      slug: "",
      validation_code: "",
      minimum_threshold: 0,
      training_data: "",
      rule: "",
    },
    auto_failed: false,
    weightage: 0,
    evaluation_type: "",
    questions: [
      {
        question: {
          internal_id: "",
          question_text: "",
          question_order: 0,
          validation_code: "",
          training_data: "",
          matching_logic: "",
          is_mandatory: false,
        },
        rule: "",
        auto_failed: false,
        weightage: 0,
      },
    ],
  };
  return categoryObj;
};

export const toCapitalize = (str: string) => {
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

// formatTime: Utility function to format time in hh:mm:ss format
export const formatTime = (
  timeInSeconds: string | number,
  isMinutes?: boolean,
): string => {
  if (
    typeof timeInSeconds === "string" &&
    timeInSeconds?.toString()?.includes(":")
  ) {
    const arrTime = timeInSeconds?.split(":");
    if (arrTime?.length > 2) {
      // Format as hh:mm:ss when hours exist
      return `${arrTime[0]}:${arrTime[1].toString().padStart(2, "0")}:${arrTime[2]?.toString().padStart(2, "0")}`;
    } else if (arrTime?.length === 2) {
      // Format as mm:ss when no hours
      return `00:${arrTime[0]}:${arrTime[1].toString().padStart(2, "0")}`;
    } else {
      // Format as mm:ss when no hours
      return `00:00:${arrTime[0].toString().padStart(2, "0")}`;
    }
  }

  let totalSeconds = Number(timeInSeconds);
  if (isMinutes) {
    totalSeconds = Math.floor(Number(timeInSeconds) * 60);
  }
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  if (hours > 0) {
    // Format as hh:mm:ss when hours exist
    return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  } else if (minutes > 0) {
    // Format as mm:ss when no hours
    return `00:${minutes}:${seconds.toString().padStart(2, "0")}`;
  } else {
    // Format as mm:ss when no hours
    return `00:00:${seconds.toString().padStart(2, "0")}`;
  }
};

// Function get Unit of time
export const getTimeUnit = (time: number | string) => {
  const seconds = parseFloat(String(time).trim());

  if (!seconds || seconds === 0) return "s";
  if (seconds >= 3600) return "h";
  if (seconds >= 60) return "m";
  return "s";
};

export const formatTimeShort = (timeInSeconds: number | string) => {
  const totalSeconds = Number(timeInSeconds);

  if (!totalSeconds) return "0";

  if (totalSeconds >= 3600) {
    const hours = Math.floor(totalSeconds / 3600);
    return `${String(hours).padStart(2, "0")}`;
  }

  if (totalSeconds >= 60) {
    const minutes = Math.floor(totalSeconds / 60);
    return `${String(minutes).padStart(2, "0")}`;
  }

  return `${String(totalSeconds).padStart(2, "0")}`;
};
export type TKnownHttpKey =
  | "RELOAD_APP"
  | "HTTP.STATUS_401"
  | "HTTP.STATUS_403"
  | "HTTP.STATUS_404"
  | "HTTP.STATUS_429"
  | "HTTP.STATUS_500"
  | "HTTP.UNKNOWN"
  | "HTTP.UNDEFINED";

export const getHttpTranslationKey = (status: any) => {
  switch (status) {
    case 401:
      return "HTTP.STATUS_401";
    case 403:
      return "HTTP.STATUS_403";
    case 404:
      return "HTTP.STATUS_404";
    case 429:
      return "HTTP.STATUS_429";
    case 500:
      return "HTTP.STATUS_500";
    default:
      return "HTTP.UNKNOWN";
  }
};

// Utility function to convert period to date range
export const getDateRangeFromPeriod = (period: string) => {
  const today = new Date();
  const day = today.getDay(); // 0 = Sunday
  // ---------- WEEK logic (Monday → Sunday) ----------
  const mondayThisWeek = new Date(today);
  mondayThisWeek.setDate(today.getDate() - day + (day === 0 ? -6 : 1));
  mondayThisWeek.setHours(0, 0, 0, 0);
  // ---------- MONTH logic (1st → last day) ----------
  const firstDayThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  switch (period) {
    case "this-week": {
      const end = new Date(mondayThisWeek);
      end.setDate(mondayThisWeek.getDate() + 6);
      end.setHours(23, 59, 59, 999);
      return {
        start_date: formatLocalDate(mondayThisWeek),
        end_date: formatLocalDate(end),
      };
    }
    case "last-week": {
      const start = new Date(mondayThisWeek);
      start.setDate(mondayThisWeek.getDate() - 7);
      const end = new Date(mondayThisWeek);
      end.setDate(mondayThisWeek.getDate() - 1);
      return {
        start_date: formatLocalDate(start),
        end_date: formatLocalDate(end),
      };
    }
    case "week-2": {
      const start = new Date(mondayThisWeek);
      start.setDate(mondayThisWeek.getDate() - 14);
      const end = new Date(mondayThisWeek);
      end.setDate(mondayThisWeek.getDate() - 8);
      return {
        start_date: formatLocalDate(start),
        end_date: formatLocalDate(end),
      };
    }
    case "week-3": {
      const start = new Date(mondayThisWeek);
      start.setDate(mondayThisWeek.getDate() - 21);
      const end = new Date(mondayThisWeek);
      end.setDate(mondayThisWeek.getDate() - 15);
      return {
        start_date: formatLocalDate(start),
        end_date: formatLocalDate(end),
      };
    }
    case "week-4": {
      const start = new Date(mondayThisWeek);
      start.setDate(mondayThisWeek.getDate() - 28);
      const end = new Date(mondayThisWeek);
      end.setDate(mondayThisWeek.getDate() - 22);
      return {
        start_date: formatLocalDate(start),
        end_date: formatLocalDate(end),
      };
    }
    case "last-month": {
      const end = new Date(firstDayThisMonth);
      end.setDate(0); // last day of previous month
      const start = new Date(end.getFullYear(), end.getMonth(), 1);
      return {
        start_date: formatLocalDate(start),
        end_date: formatLocalDate(end),
      };
    }
    case "month-2": {
      const endOfLastMonth = new Date(firstDayThisMonth);
      endOfLastMonth.setDate(0);
      const firstDayOfLastMonth = new Date(
        endOfLastMonth.getFullYear(),
        endOfLastMonth.getMonth(),
        1,
      );
      const end = new Date(firstDayOfLastMonth);
      end.setDate(0);
      const start = new Date(end.getFullYear(), end.getMonth(), 1);
      return {
        start_date: formatLocalDate(start),
        end_date: formatLocalDate(end),
      };
    }
    case "month-3": {
      const endOfLastMonth = new Date(firstDayThisMonth);
      endOfLastMonth.setDate(0);
      const firstDayOfLastMonth = new Date(
        endOfLastMonth.getFullYear(),
        endOfLastMonth.getMonth(),
        1,
      );
      const endOfTwoMonthsAgo = new Date(firstDayOfLastMonth);
      endOfTwoMonthsAgo.setDate(0);
      const firstDayOfTwoMonthsAgo = new Date(
        endOfTwoMonthsAgo.getFullYear(),
        endOfTwoMonthsAgo.getMonth(),
        1,
      );
      const end = new Date(firstDayOfTwoMonthsAgo);
      end.setDate(0);
      const start = new Date(end.getFullYear(), end.getMonth(), 1);
      return {
        start_date: formatLocalDate(start),
        end_date: formatLocalDate(end),
      };
    }
    default: {
      const todayStr = formatLocalDate(today);
      return { start_date: todayStr, end_date: todayStr };
    }
  }
};

/**
 * Safely parses a string to a Date object if valid.
 * @param value The date string to parse.
 * @param fallback The value to return if the date is invalid or null/undefined.
 */
export const safeParseDate = (
  value?: string | null,
  fallback?: Date,
): Date | undefined => {
  if (!value) return fallback;
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : fallback;
};

export const isAnyFeatureEnabled = (slugs: string[]) => {
  const featureFlags = useStore?.getState().featureShowHideFlag;
  const user = useStore?.getState().user;
  const isAdmin = isAdminLike(user);
  if(isAdmin) return true;
  return (
    Array.isArray(featureFlags) &&
    slugs?.some((slug) => featureFlags?.includes(slug))
  );
};

// Function to check  Is Feature Enable
export const isFeatureEnabled = (slug: string): boolean => {
  const featureFlags = useStore.getState().featureShowHideFlag;
  const user = useStore.getState().user;
  const isAdmin = isAdminLike(user);
  if(isAdmin) return true;
  return Array.isArray(featureFlags) && featureFlags?.includes(slug);
};

// Get HSL values from CSS variables
export const getHSLColor = (variable: string) => {
  if (typeof window !== "undefined") {
    const root = document.documentElement;
    const hslValue = getComputedStyle(root).getPropertyValue(variable).trim();
    return `hsl(${hslValue})`;
  }
  return "hsl(0, 0%, 50%)"; // Fallback color
};

/**
 * Utility function to get last 7 days (including today)
 * Returns startDate and endDate in ISO format (YYYY-MM-DD)
 */
export const getLastSevenDays = (): { startDate: string; endDate: string } => {
  const today = new Date();
  const prior = new Date();
  prior.setDate(today.getDate() - 6); // 7 days including today
  return { startDate: formatLocalDate(prior), endDate: formatLocalDate(today) };
};

// get Score Color
export const getScoreColor = (score: string) => {
  // const numScore = parseInt(score?.indexOf("%") ? score?.replace("%", "") : score);
  const numScore = typeof score === "string" ? parseInt(score) : score;
  if (numScore >= 75) return "text-green";
  if (numScore >= 50) return "text-amber";
  return "text-destructive";
};

// Trend Calculation function
export const getTrendDirection = (value: number) => {
  return value > 0 ? true : false;
};

// Format File size in KB,MB
export function formatFileSize(bytes: number) {
  const units = ["Bytes", "KB", "MB", "GB", "TB"];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size = size / 1024;
    unitIndex++;
  }

  return `${size.toFixed(2)} ${units[unitIndex]}`;
}

// Normalizing String
export const normalizeString = (value: unknown) =>
  typeof value === "string" ? value : "";

//  Normalize Json String
export const normalizeJsonString = (value: unknown) =>
  typeof value === "string" ? value : JSON.stringify(value ?? "");

export const reviewBadgeColor = (color: string | null) => {
  switch (color) {
    case "yes":
      return "bg-green";
    case "no":
      return "bg-[#ff3e1d9e]";
    default:
      return "bg-blue";
  }
};
// Safe to String -
export const safeToString = (value: unknown) => {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (
    typeof value === "number" ||
    typeof value === "boolean" ||
    typeof value === "bigint"
  ) {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return "[unprintable]";
  }
};

export const getSingleInitials = (name: string) =>
  name?.trim()?.charAt(0)?.toUpperCase();

// valid regex validation
export const hasRegexMetaChar = (val: string) => {
  return /[\^$.*+?()[\]{}|\\]/.test(val);
};

export const getProgressBarColors = (percentage: number) => {
  if (percentage > 80) return "green";
  if (percentage > 60) return "amber";
  if (percentage > 0) return "destructive";

  return "gray";
};
